import { ReactElement, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import useDebounce from 'hooks/useDebounce'
import {
	useEquipmentMakeByIdQuery,
	useEquipmentMakesQuery,
	useEquipmentModelByIdQuery,
	useEquipmentModelsQuery,
} from '../../../../../graphql'

type MakeOption = {
	equipment_make_id: number,
	name: string,
}

type ModelOption = {
	equipment_model_id: number,
	name: string,
	equipment_make_id: number,
}

type Props = {
	makeId: number | null,
	modelId: number | null,
	onChange: ( next: { makeId: number | null, modelId: number | null } ) => void,
	disabled?: boolean,
	required?: boolean,
}

// Combined picker: selecting a make wipes the current model selection (the
// model only makes sense in the context of its make). The model autocomplete
// is disabled until a make is chosen.
export default function EquipmentMakeModelPicker( {
	makeId,
	modelId,
	onChange,
	disabled = false,
	required = false,
}: Props ): ReactElement {
	const [ makeInput, setMakeInput ] = useState( '' )
	const [ modelInput, setModelInput ] = useState( '' )
	const debouncedMakeSearch = useDebounce( makeInput, 300 )
	const debouncedModelSearch = useDebounce( modelInput, 300 )

	const { data: makesData, loading: makesLoading } = useEquipmentMakesQuery( {
		variables: { name: debouncedMakeSearch || undefined },
		errorPolicy: 'ignore',
	} )

	const { data: selectedMakeData } = useEquipmentMakeByIdQuery( {
		variables: { id: makeId ?? 0 },
		skip: !makeId,
		errorPolicy: 'ignore',
	} )

	const { data: modelsData, loading: modelsLoading } = useEquipmentModelsQuery( {
		variables: {
			equipmentMakeId: makeId ?? 0,
			name: debouncedModelSearch || undefined,
		},
		skip: !makeId,
		errorPolicy: 'ignore',
	} )

	const { data: selectedModelData } = useEquipmentModelByIdQuery( {
		variables: { id: modelId ?? 0 },
		skip: !modelId,
		errorPolicy: 'ignore',
	} )

	const makeOptions: MakeOption[] = ( makesData?.equipmentMakes ?? [] ).slice()
	const selectedMake = selectedMakeData?.equipmentMake ?? null
	if ( selectedMake && !makeOptions.find( ( o ) => o.equipment_make_id === selectedMake.equipment_make_id ) ) {
		makeOptions.unshift( selectedMake )
	}

	const modelOptions: ModelOption[] = ( modelsData?.equipmentModels ?? [] ).slice()
	const selectedModel = selectedModelData?.equipmentModel ?? null
	if ( selectedModel && !modelOptions.find( ( o ) => o.equipment_model_id === selectedModel.equipment_model_id ) ) {
		modelOptions.unshift( selectedModel )
	}

	const handleMakeChange = ( next: MakeOption | null ) => {
		const nextId = next?.equipment_make_id ?? null
		if ( nextId !== makeId ) {
			// Picking a different make invalidates the current model.
			onChange( { makeId: nextId, modelId: null } )
		}
	}

	const handleModelChange = ( next: ModelOption | null ) => {
		onChange( { makeId, modelId: next?.equipment_model_id ?? null } )
	}

	return (
		<Stack spacing={2}>
			<Autocomplete<MakeOption>
				value={selectedMake}
				onChange={( _, newValue ) => handleMakeChange( newValue )}
				onInputChange={( _, newInput ) => setMakeInput( newInput )}
				options={makeOptions}
				getOptionLabel={( opt ) => opt.name}
				isOptionEqualToValue={( opt, val ) => opt.equipment_make_id === val.equipment_make_id}
				loading={makesLoading}
				disabled={disabled}
				size="small"
				renderInput={( params ) => (
					<TextField
						{...params}
						label="Equipment Make"
						required={required}
						error={required && !makeId}
						helperText={required && !makeId ? 'Required when Numina Serial Type is Asset' : undefined}
					/>
				)}
			/>
			<Autocomplete<ModelOption>
				value={selectedModel}
				onChange={( _, newValue ) => handleModelChange( newValue )}
				onInputChange={( _, newInput ) => setModelInput( newInput )}
				options={modelOptions}
				getOptionLabel={( opt ) => opt.name}
				isOptionEqualToValue={( opt, val ) => opt.equipment_model_id === val.equipment_model_id}
				loading={modelsLoading}
				disabled={disabled || !makeId}
				size="small"
				renderInput={( params ) => (
					<TextField
						{...params}
						label="Equipment Model"
						required={required}
						error={required && !!makeId && !modelId}
						helperText={
							!makeId
								? 'Select an Equipment Make first'
								: required && !modelId
									? 'Required when Numina Serial Type is Asset'
									: undefined
						}
					/>
				)}
			/>
		</Stack>
	)
}
